import dayjs from "dayjs"
import type { LoaderFunction } from "react-router-dom"
import { isProd } from "../config"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { getFromApi } from "./utils"

const getLatestFeedingStats = async (orgId: string): Promise<any | null> => {
  console.log("> getLatestFeedingStats")
  const apiName = "frontendApi"
  const path = `/feeding-stats/${orgId}/latest`

  if (!(await isUserSignedIn())) {
    return null
  }

  try {
    console.log("getLatestFeedingStats: Uncached. Loading from API...")
    let data = (await getFromApi(apiName, path)).json
    !isProd && console.debug("getLatestFeedingStats: Fetched from api:", data)
    // !isProd && console.debug("✅ getLatestFeedingStats: data")
    return await data
  } catch (err) {
    console.error(
      "getLatestFeedingStats: Error fetching latest FeedingStats data from api."
    )
    !isProd && console.debug("getLatestFeedingStats: api name =", apiName)
    console.log("getLatestFeedingStats: Error:", err)
    // !isProd && console.debug("❌ getLatestFeedingStats: throw")
    throw err
  }
}

export const getDefaultDateRange = (now: Date) => {
  /** @todo make a hook */
  const userPrefDateRange = localStorage.getItem("userPref:dateRange")
  if (userPrefDateRange) {
    const datesAsStrings = JSON.parse(userPrefDateRange)
    if (datesAsStrings.startDate !== null && datesAsStrings.endDate !== null) {
      const dateRange = {
        startDate: new Date(datesAsStrings.startDate),
        endDate: new Date(datesAsStrings.endDate),
      }
      return dateRange
    }
  }

  const lastWeek: Date = dayjs().subtract(6, "days").toDate()
  lastWeek.setHours(0, 0, 0, 0)
  const today = new Date(now) // clone
  today.setHours(23, 59, 59)
  const dateRange = {
    startDate: lastWeek,
    endDate: today,
  }
  return dateRange
}

export const getCustomRangeFeedingStats = async (
  orgId: string,
  startDate: string | Date,
  endDate: string | Date
): Promise<any | null> => {
  console.log("> FeedingStatsApi.getCustomRangeFeedingStats")
  if (typeof startDate !== "string") {
    startDate = startDate.toISOString()
  }
  if (typeof endDate !== "string") {
    endDate = endDate.toISOString()
  }
  const apiName = "frontendApi"
  const path = `/feeding-statistics-custom/${orgId}?startDate=${startDate}&endDate=${endDate}`

  if (!(await isUserSignedIn())) {
    return null
  }

  try {
    console.log("getCustomRangeFeedingStats: Uncached. Loading from API...")
    let data = (await getFromApi(apiName, path)).json
    !isProd &&
      console.debug("getCustomRangeFeedingStats: Fetched from api:", data)
    // !isProd && console.debug("✅ getCustomRangeFeedingStats: data")
    return await data
  } catch (err) {
    console.error(
      "getCustomRangeFeedingStats: Error fetching custom FeedingStats data from api."
    )
    !isProd && console.debug("getCustomRangeFeedingStats: api name =", apiName)
    console.log("getCustomRangeFeedingStats: Error:", err)
    // !isProd && console.debug("❌ getCustomRangeFeedingStats: throw")
    throw err
  }
}

const loadLatestFeedingStatsData = async (
  orgId: string
): Promise<any | undefined> => {
  console.log("> loadLatestFeedingStatsData")
  const feedingStatsData = await getLatestFeedingStats(orgId)
  if (!feedingStatsData) {
    console.error("loadLatestFeedingStatsData: got no FeedingStats data")
    return undefined
  }
  // console.log("loadLatestFeedingStatsData: got FeedingStats data")
  !isProd && console.log("< loadLatestFeedingStatsData")
  return feedingStatsData
}

const loadCustomRangeFeedingStats = async (
  orgId: string,
  startDate: string | Date,
  endDate: string | Date
): Promise<any | undefined> => {
  console.debug("> FeedingStatsApi.loadCustomRangeFeedingStats")
  const feedingStatsData = await getCustomRangeFeedingStats(
    orgId,
    startDate,
    endDate
  )
  if (!feedingStatsData) {
    console.error("loadCustomRangeFeedingStats: got no FeedingStats data")
    return undefined
  }
  // console.log("loadCustomRangeFeedingStats: got FeedingStats data")
  !isProd && console.log("< loadCustomRangeFeedingStats")
  return feedingStatsData
}

const loadFeedingStats = async (orgId: string): Promise<any | null> => {
  const now = new Date()
  const dateRange = getDefaultDateRange(now)
  const startDate = dateRange.startDate.toISOString()
  const endDate = dateRange.endDate.toISOString()
  !isProd && console.debug("loadFeedingStats: orgId = ", orgId)
  if (endDate === now.toISOString()) {
    // The user has not chosen a custom date range.
    // Load the precomputed "latest" dataset.
    try {
      !isProd && console.debug("loadFeedingStats: loading 'latest' dataset")
      const feedingStatsData = await loadLatestFeedingStatsData(orgId)
      if (feedingStatsData) {
        return feedingStatsData
      }
    } catch {
      /* empty */
    }
  }
  !isProd && console.debug("loadFeedingStats: loading 'custom' dataset")
  !isProd && console.debug("loadFeedingStats: startDate = ", startDate)
  !isProd && console.debug("loadFeedingStats: endDate = ", endDate)
  try {
    const feedingStatsData = await loadCustomRangeFeedingStats(
      orgId,
      startDate,
      endDate
    )
    if (feedingStatsData) {
      return feedingStatsData
    }
  } catch {
    /* empty */
  }
  return null
}

/**
 * Returns `feedingStats` as an unresolved promise (rather than awaiting it),
 * letting the route render immediately. Consumers show a placeholder via
 * <Suspense>/<Await> until it resolves.
 */
export const loaderCustomRange: LoaderFunction = ({ params }) => {
  console.log("> FeedingStatsApi.loaderCustomRange")
  if (!params["orgId"]) {
    console.error("loaderCustomRange: param orgId is missing")
    return { feedingStats: Promise.resolve(null) }
  }
  return { feedingStats: loadFeedingStats(params["orgId"]) }
}
